// INTRO-2 — first-use chat intros. Content is what you can ASK Aidey on each screen,
// never what the screen does (that is the tour's job). Fires only on a LATER visit,
// after the screen's tour is terminal (or, for screens with no auto-tour, on first
// visit), never overlapping an open guide. Capped at THREE per user, ever.
//
// AMENDMENT 3 (fact check): every example below maps to a real, working tool in
// routes/aidey.js, verified against its executeTool handler. The `tools` array on
// each screen lists the exact tool each phrase invokes, and the test asserts each is
// a real tool name. Nothing was dropped; two rewordings were made for accuracy:
//   • Invoicing "chase my overdue invoices" -> "Chase an overdue invoice"
//     (send_invoice_chase acts on ONE overdue invoice, so the phrase is singular).
//   • Expenses dropped "approve a pending expense" (approve_expense needs a specific
//     expense/notification context, not a clean cold ask) in favour of
//     get_financial_summary, which works unprompted.

export const CHAT_INTROS = {
  dashboard: {
    gateOnTour: true,
    examples: [
      'Create a task to call the accountant tomorrow',
      'What tasks are overdue?',
      'What is on my schedule today?',
    ],
    tools: ['create_task', 'get_overdue_tasks', 'get_schedule'],
  },
  invoicing: {
    gateOnTour: true,
    examples: [
      'Create an invoice for a client',
      'Which invoices are overdue?',
      'Chase an overdue invoice',
    ],
    tools: ['create_invoice', 'list_overdue_invoices', 'send_invoice_chase'],
  },
  expenses: {
    gateOnTour: true,
    examples: [
      'Log a £40 expense for train travel',
      'Give me a financial summary for this month',
    ],
    tools: ['log_expense', 'get_financial_summary'],
  },
  crm: {
    gateOnTour: true,
    examples: [
      'Add a contact for Jane at Acme',
      'Who do I need to follow up with?',
      'Are any stage reminders due?',
    ],
    tools: ['add_contact', 'get_crm_followups', 'get_stage_reminders'],
  },
  scheduling: {
    gateOnTour: true,
    examples: [
      'When is the team free on Thursday?',
      'Reschedule a meeting',
      'Show my event proposals',
    ],
    tools: ['get_team_availability', 'reschedule_meeting', 'list_event_proposals'],
  },
  hr: {
    gateOnTour: true,
    examples: [
      'What is my leave balance?',
      'Request time off next Friday',
      'Show pending leave requests',
    ],
    tools: ['get_leave_balance', 'submit_leave_request', 'get_leave_requests'],
  },
  budget: {
    gateOnTour: true,
    examples: [
      'Add a £200 broadband bill to my budget',
      'Set a savings goal of £1000 for a holiday',
      'How is my budget looking?',
    ],
    tools: ['add_budget_item', 'add_savings_goal', 'get_budget_summary'],
  },
  projects: {
    gateOnTour: true,
    examples: [
      'Create a project for the Acme website',
      'What is the status of my projects?',
      'Put the Acme project on hold',
    ],
    tools: ['create_project', 'get_project_status', 'update_project_status'],
  },
  // No auto-tour on these two, so there is no tour to collide with: fire on first visit.
  mealplanner: {
    gateOnTour: false,
    examples: [
      'Suggest some meals for this week',
      'Plan spaghetti for Tuesday dinner',
      'What is on the meal plan?',
    ],
    tools: ['suggest_meals', 'plan_meal', 'get_meal_plan'],
  },
  renewals: {
    gateOnTour: false,
    examples: [
      'Add a renewal for my car insurance',
      'What renewals are coming up?',
    ],
    tools: ['add_renewal', 'list_renewals'],
  },
};

export const INTRO_CAP = 3;

// Build the assistant message for a screen. The THIRD (final) intro a user ever sees
// carries the help-button line; after it, no further intros fire.
export function buildIntroBody(tourId, isThird) {
  const c = CHAT_INTROS[tourId];
  if (!c) return '';
  const lines = c.examples.map((e) => `- "${e}"`).join('\n');
  let body = `On this screen you can just ask me to get things done for you. For example:\n${lines}\n\nType whatever you need and I will take care of it.`;
  if (isThird) {
    body += `\n\nOne more thing: you can tap the ? button on any screen at any time for a quick walkthrough. I will not pop up like this again.`;
  }
  return body;
}

// Pure decision: should the intro fire for this screen right now, and is it the third
// (final) one? Keeps all the gating logic testable without React.
//   progress: the tour-progress map ({ [id]: { status } }), which also carries the
//             chat:<screen> records — the cap is DERIVED from how many exist.
export function decideIntro({ tourId, gateOnTour, hasSeenTour, guideOpen, progress = {} }) {
  if (!CHAT_INTROS[tourId]) return { fire: false };        // not an eligible screen
  if (gateOnTour && !hasSeenTour) return { fire: false };  // tour still due -> never co-fire
  if (guideOpen) return { fire: false };                   // never overlap an open guide
  if (progress[`chat:${tourId}`]) return { fire: false };  // already shown for this screen
  const fired = Object.keys(progress).filter((k) => k.startsWith('chat:')).length;
  if (fired >= INTRO_CAP) return { fire: false };          // cap: three ever
  return { fire: true, isThird: fired === INTRO_CAP - 1 };
}
